import { CloudOff } from 'lucide-react';
import { useIntl } from 'react-intl';
import EmptyState from './EmptyState';
import { useResponsiveLayout } from '../../hooks/ui/useResponsiveLayout';

/**
 * The ONE "couldn't load" state (KAN-590 / RD-ERR-001). Render it when a read
 * failed and there is NO data to show. If data is already on screen, keep it and
 * report the failed refresh with a toast instead — never replace data with this.
 *
 * Never shows server text: copy is the generic title + a connection hint.
 *
 * @param {object}   props
 * @param {() => void} props.onRetry   required — re-runs the failed read
 * @param {boolean}  [props.retrying]  retry in flight: button dims, label "Trying…"
 * @param {'standard'|'inline'} [props.variant]  'standard' = centered card (screens,
 *        lists); 'inline' = compact row for widgets / drawer sections / tiles
 * @param {string}   [props.testId]    data-testid on a wrapper
 *
 * @example
 *   {loadFailed && !rows.length && <LoadErrorState onRetry={reload} retrying={loading} />}
 *   <LoadErrorState variant="inline" onRetry={reload} />
 */
export default function LoadErrorState({ onRetry, retrying = false, variant = 'standard', testId }) {
  const intl = useIntl();
  const { isTouch } = useResponsiveLayout();
  const inline = variant === 'inline';
  const label = retrying
    ? intl.formatMessage({ id: 'errors.load.retrying', defaultMessage: 'Trying…' })
    : intl.formatMessage({ id: 'errors.load.retry', defaultMessage: 'Try again' });
  const message = inline
    ? intl.formatMessage({ id: 'errors.load.messageShort', defaultMessage: 'Check your connection.' })
    : intl.formatMessage({ id: 'errors.load.message', defaultMessage: 'Check your connection and try again.' });
  return (
    <div data-testid={testId} role="alert">
      <EmptyState
        icon={CloudOff}
        tone="muted"
        variant={variant}
        touch={isTouch}
        title={intl.formatMessage({ id: 'errors.load.title', defaultMessage: "Couldn't load this" })}
        message={message}
        action={{ label, onClick: onRetry, busy: retrying, variant: 'tertiary' }}
      />
    </div>
  );
}
